import "./CreateSpecialHoursPopup.scss";
import { useEffect, useState } from "react";
import Popup from "../../../hoc/Popup/Popup";
import { toast } from "react-toastify";
import HandleConfirmationPopup from "../../HandleConfirmation/HandleConfirmationPopup";
import { ISpecialHoursFacility } from "../../../types/facility";
import { createSpecialHours } from "../../../api/facility/facilityOpeningTimes";
import WeekDaySelector from "../../../hoc/DatePicker/Datepicker";
import { listCourts } from "../../../api/courts/courts";
import { ICourt } from "../../../types/api";
import { formatToIsoDate } from "../../../shared/dateUtils";
import {
  IOpeningHoursConflict,
  findOpeningHoursConflicts,
} from "../openingHoursConflicts";
import OpeningHoursConflictConfirmation from "../OpeningHoursConflictConfirmation";

type FacilityCourtType = "INDOOR" | "OUTDOOR" | "GYM" | "REHAB";

// The same setting is nearly always wanted for several court types at once,
// so creation is no longer locked to the tab that is open.
const COURT_TYPE_OPTIONS: { value: FacilityCourtType; label: string }[] = [
  { value: "INDOOR", label: "Inomhus" },
  { value: "OUTDOOR", label: "Utomhus" },
  { value: "REHAB", label: "Rehab" },
];

interface ICreateSpecialHoursPopupProps {
  showPopup: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function CreateSpecialHoursPopup(props: ICreateSpecialHoursPopupProps) {
  const { showPopup, onClose, onConfirm } = props;
  const [showHandleConfirmationPopup, setShowHandleConfirmationPopup] =
    useState(false);
  const [showDoubleConfirmationPopup, setShowDoubleConfirmationPopup] =
    useState(false);
  const handleConfirm = () => {
    onConfirm();
    handleClose();
  };
  const handleClose = () => {
    setSpecialHourData(initialSpecialData);
    setSelectedCourtTypes([]);
    setStartTime("07:00:00");
    setEndTime("23:00:00");
    onClose();
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [date, setDate] = useState(new Date());
  const [startTime, setStartTime] = useState("07:00:00");
  const [endTime, setEndTime] = useState("23:00:00");
  const initialSpecialData: ISpecialHoursFacility = {
    date: new Date().toISOString(),
    opening_hours: "07:00:00",
    closing_hours: "23:00:00",
    court_type: COURT_TYPE_OPTIONS[0].value,
    alias: "",
    closed: false,
  };

  const [specialHourData, setSpecialHourData] =
    useState<ISpecialHoursFacility>(initialSpecialData);

  // Starts empty and has to be chosen. Defaulting to every court meant a
  // mis-click wrote three settings instead of one, and the writes are not
  // atomic — undoing that is three deletions across three lists.
  const [selectedCourtTypes, setSelectedCourtTypes] = useState<
    FacilityCourtType[]
  >([]);

  // Bookings the change would make unbookable, listed in the second confirm.
  const [conflicts, setConflicts] = useState<IOpeningHoursConflict[]>([]);
  const [courts, setCourts] = useState<ICourt[]>([]);
  // An empty conflicts list means "none" only if the lookup actually ran.
  const [conflictCheckFailed, setConflictCheckFailed] = useState(false);
  /**
   * Between the two confirmations: look up what the change would hide, so the
   * second one can name the bookings instead of warning in the abstract.
   */
  const prepareDoubleConfirmation = async () => {
    setShowHandleConfirmationPopup(false);
    // Several queries plus a name lookup per rehab member, so this is long
    // enough to look like nothing happened when the first popup closes.
    const checkingToast = toast.loading("Kontrollerar bokningar...");
    try {
      const found = await findOpeningHoursConflicts({
        date: formatToIsoDate(date).split("T")[0],
        courtTypes: selectedCourtTypes,
        closed: specialHourData.closed,
        openingHours: startTime,
        closingHours: endTime,
      });
      setConflicts(found);
      setConflictCheckFailed(false);
      toast.dismiss(checkingToast);
    } catch (error: any) {
      // A failed lookup must not read as "no bookings", and must not block
      // either — an outage in the query would otherwise lock the whole screen.
      setConflictCheckFailed(true);
      toast.update(checkingToast, {
        render: error?.message ?? "Kunde inte kontrollera bokningar",
        type: "error",
        isLoading: false,
        autoClose: 4000,
      });
      setConflicts([]);
    } finally {
      setShowDoubleConfirmationPopup(true);
    }
  };

  // Only for turning a court_id into "B4" in the list.
  useEffect(() => {
    if (!showPopup || courts.length) return;
    listCourts()
      .then(setCourts)
      .catch(() => setCourts([]));
  }, [showPopup]);

  const toggleCourtType = (type: FacilityCourtType) => {
    setSelectedCourtTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const weekdays = {
    Måndag: "monday",
    Tisdag: "tuesday",
    Onsdag: "wednesday",
    Torsdag: "thursday",
    Fredag: "friday",
    Lördag: "saturday",
    Söndag: "sunday",
  };
  const handleTimeChange = (
    timeType: "start" | "end",
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const timeValue = e.target.value;
    if (timeType === "start") {
      setStartTime(timeValue);
    } else {
      setEndTime(timeValue);
    }
  };

  const handleTimeBlur = (
    timeType: "start" | "end",
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    let time = e.target.value;
    let parts = time.split(":");

    if (parts.length === 2) {
      time += ":00";
    } else if (parts.length === 1 && time.includes(":")) {
      time += "00:00";
    } else if (parts.length === 1) {
      time = time.padStart(2, "0") + ":00:00";
    }

    if (timeType === "start") {
      setStartTime(time);
    } else {
      setEndTime(time);
    }
  };

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSpecialHourData({
      ...specialHourData,
      [e.target.name]: e.target.value,
    });
  };
  const handleClosedToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isClosed = e.target.checked;

    setSpecialHourData((prev) => ({
      ...prev,
      closed: isClosed,
    }));
  };
  const handleCreateSpecialHours = async () => {
    setLoading(true);
    specialHourData.opening_hours = startTime;
    specialHourData.closing_hours = endTime;
    specialHourData.date = date.toISOString();
    if (specialHourData.closing_hours < specialHourData.opening_hours) {
      toast.error("Stängningstiden kan inte vara tidigare än öppningstiden");
      setLoading(false);
      return;
    }
    if (specialHourData.alias === "") {
      toast.error("Namn är obligatoriskt");
      setLoading(false);
      return;
    }
    if (selectedCourtTypes.length === 0) {
      toast.error("Välj minst en typ av bana");
      setLoading(false);
      return;
    }

    // One write per court type. These are not atomic — a single type can fail
    // (e.g. the day already has a setting) while the others succeed, so report
    // per type instead of one blanket success.
    const succeeded: string[] = [];
    const failed: string[] = [];

    for (const type of selectedCourtTypes) {
      const label =
        COURT_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
      try {
        const response = await createSpecialHours({
          ...specialHourData,
          court_type: type,
        });
        if (response && !("code" in response)) {
          succeeded.push(label);
        } else {
          failed.push(label);
        }
      } catch (error: any) {
        failed.push(label);
      }
    }

    setLoading(false);

    if (succeeded.length > 0) {
      toast.success(`Dagsaktuell inställning skapad: ${succeeded.join(", ")}`);
    }
    if (failed.length > 0) {
      toast.error(`Misslyckades för: ${failed.join(", ")}`);
    }
    if (succeeded.length > 0) {
      handleConfirm();
    }
  };

  useEffect(() => {
    if (showPopup) {
      setSpecialHourData(initialSpecialData);
      setSelectedCourtTypes([]);
      setConflicts([]);
    }
  }, [showPopup]);
  return (
    <Popup
      showPopup={props.showPopup}
      onClose={handleClose}
      leftTopElement={
        <span className="text-m">Skapa dagsaktuell inställning</span>
      }
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form"
          onSubmit={(e) => {
            e.preventDefault();
            setShowHandleConfirmationPopup(true);
          }}
        >
          <div className="general-popup-form-group">
            <div className="general-popup-form-field">
              <label htmlFor="alias" className="text-s">
                Namn
              </label>
              <input
                type="text"
                name="alias"
                value={specialHourData.alias}
                onChange={handleInputChange}
                className="text-m"
              />
              <label htmlFor="date" className="text-s">
                Datum
              </label>
              <WeekDaySelector
                textM
                includeWeekdays={false}
                value={date}
                onChange={(date) => setDate(date)}
              />
              <label htmlFor="courtType" className="text-s">
                Typ av bana
              </label>
              {COURT_TYPE_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  className="general-popup-flex-between"
                >
                  <span className="text-m secondary-text">{option.label}</span>
                  <label className="toggle text-m">
                    <input
                      type="checkbox"
                      name={`court_type_${option.value}`}
                      checked={selectedCourtTypes.includes(option.value)}
                      onChange={() => toggleCourtType(option.value)}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              ))}
              {!specialHourData.closed && (
                <>
                  <div className="general-popup-flex-between">
                    <label htmlFor="memberType" className="text-s">
                      Start
                    </label>
                    <input
                      type="time"
                      id="startTime"
                      value={startTime}
                      className="text-m"
                      onBlur={(e) => handleTimeBlur("start", e)}
                      onChange={(e) => handleTimeChange("start", e)}
                    />
                  </div>

                  <div className="general-popup-flex-between">
                    <label htmlFor="memberType" className="text-s">
                      Slut
                    </label>
                    <input
                      type="time"
                      id="endTime"
                      value={endTime}
                      className="text-m"
                      onBlur={(e) => handleTimeBlur("end", e)}
                      onChange={(e) => handleTimeChange("end", e)}
                    />
                  </div>
                </>
              )}
              <label htmlFor="closed" className="text-m">
                Ska det vara stängt under detta interval ?
              </label>
              <div className="general-popup-flex-between">
                <span className="text-m secondary-text">Stängt</span>
                <label className="toggle text-m">
                  <input
                    type="checkbox"
                    name="closed"
                    checked={specialHourData.closed}
                    onChange={(e) => handleClosedToggle(e)}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="general-popup-bottom-buttons-wrapper flex-end">
            <button type="submit" className="text-m">
              Skapa dagsaktuella inställning
            </button>
          </div>
        </form>
      </div>

      <HandleConfirmationPopup
        showPopup={showHandleConfirmationPopup}
        onClose={() => setShowHandleConfirmationPopup(false)}
        onConfirm={prepareDoubleConfirmation}
        confirmText="Ja"
        questionText="Är du säker på att du vill skapa upp denna dagsaktuella inställning?"
        denyText="Avbryt"
      />
      <OpeningHoursConflictConfirmation
        showPopup={showDoubleConfirmationPopup}
        onClose={() => setShowDoubleConfirmationPopup(false)}
        onConfirm={() => {
          handleCreateSpecialHours();
          setShowDoubleConfirmationPopup(false);
        }}
        conflicts={conflicts}
        courts={courts}
        checkFailed={conflictCheckFailed}
      />
    </Popup>
  );
}

export default CreateSpecialHoursPopup;
